import React, { useRef, useState, useCallback } from "react";
import { Alert } from "@mui/material";

import { Container } from "./styles.js";

import LinearProgress from "@mui/material/LinearProgress";

// Utility function to format file size
const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const FileUploadDropzone = ({
  onFilesSelected,
  onFileRemove, // <-- New prop added here
  maxFiles = 1,
  maxSize = 5 * 1024 * 1024,
  accept = "image/*",
  className = "",
  showProgress = false,
  uploadTypeText = "SVG, PNG, JPG or GIF (2MB max)",
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState([]);
  const dropzoneRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback(
    (files) => {
      const validFiles = Array.from(files).filter((file) => {
        if (file.size > maxSize) {
          setError(
            `File ${file.name} is too large (${(
              file.size /
              1024 /
              1024
            ).toFixed(2)}MB, max ${maxSize / 1024 / 1024}MB)`,
          );
          return false;
        }
        const acceptPattern = accept
          .split(",")
          .map((type) => type.trim().replace("*", ".*"))
          .join("|");
        if (
          !file.type ||
          !file.type.match(new RegExp(`^(${acceptPattern})$`, "i"))
        ) {
          setError(
            `File ${file.name} has invalid type (${
              file.type || "unknown"
            }); accepted: ${accept}`,
          );
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        setError("");
        setUploadProgress(
          validFiles.slice(0, maxFiles).map((file) => ({
            fileName: file.name,
            fileSize: file.size, // <-- Store file size here
            progress: 0,
          })),
        );
        onFilesSelected(
          validFiles.slice(0, maxFiles),
          setUploadProgress,
          setError,
          setSuccess,
        );
      } else if (files.length > 0) {
        setError(
          `No valid files selected. Check file types (${accept}) and size (max ${
            maxSize / 1024 / 1024
          }MB).`,
        );
      }
    },
    [onFilesSelected, maxSize, accept, maxFiles],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  const handleChange = useCallback(
    (e) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
        e.target.value = "";
      }
    },
    [handleFiles],
  );

  // <-- New function to handle removal
  const handleRemove = (indexToRemove) => {
    setUploadProgress((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
    setSuccess("");
    setError("");
    if (onFileRemove) onFileRemove(indexToRemove);
  };

  return (
    <Container>
      <div
        ref={dropzoneRef}
        className={`dropzone ${dragActive ? "drag-active" : ""} ${className}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() =>
          dropzoneRef.current?.querySelector('input[type="file"]')?.click()
        }
      >
        <input
          type="file"
          multiple={maxFiles > 1}
          accept={accept}
          onChange={handleChange}
          style={{ display: "none" }}
        />
        <div className="dropzone-content">
          <div className="icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.66675 13.3333L10.0001 10M10.0001 10L13.3334 13.3333M10.0001 10V17.5M16.6667 13.9524C17.6847 13.1117 18.3334 11.8399 18.3334 10.4167C18.3334 7.88536 16.2814 5.83333 13.7501 5.83333C13.568 5.83333 13.3976 5.73833 13.3052 5.58145C12.2185 3.73736 10.2121 2.5 7.91675 2.5C4.46497 2.5 1.66675 5.29822 1.66675 8.75C1.66675 10.4718 2.36295 12.0309 3.48921 13.1613"
                stroke="#FEFEFE"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p>
            <strong>Click to upload</strong> or drag and drop
          </p>
          <span className="file-types">{uploadTypeText}</span>
          {error && (
            <Alert severity="error" sx={{ mt: 4 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 4 }}>
              {success}
            </Alert>
          )}
        </div>
      </div>
      {showProgress && uploadProgress.length > 0 && (
        <div className="progress-container">
          {/* --> Updated mapping to display fileSize and remove button <-- */}
          {uploadProgress.map(({ fileName, fileSize, progress }, index) => (
            <div key={index} className="progress-item">
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{fileName}</span>
                {fileSize && (
                  <span style={{ fontSize: "0.8em", color: "gray" }}>
                    {formatBytes(fileSize)}
                  </span>
                )}
              </div>
              <div className="file-actions">
                <LinearProgress
                  id="login-loading"
                  color="secondary"
                  sx={{
                    height: "8px",
                    borderRadius: "6px",
                    flex: 1, // <-- This tells it to fill the available width!
                  }}
                  variant="determinate"
                  value={progress}
                />
                <span>{progress}%</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent re-triggering the file input
                    handleRemove(index);
                  }}
                  style={{
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    color: "red",
                    display: "flex", // Keeps the SVG perfectly centered
                  }}
                >
                  <svg
                    width="17"
                    height="19"
                    viewBox="0 0 17 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.6667 4.16683V3.50016C11.6667 2.56674 11.6667 2.10003 11.485 1.74351C11.3252 1.42991 11.0703 1.17494 10.7567 1.01515C10.4001 0.833496 9.93342 0.833496 9 0.833496H7.66667C6.73325 0.833496 6.26654 0.833496 5.91002 1.01515C5.59641 1.17494 5.34145 1.42991 5.18166 1.74351C5 2.10003 5 2.56674 5 3.50016V4.16683M6.66667 8.75016V12.9168M10 8.75016V12.9168M0.833336 4.16683H15.8333M14.1667 4.16683V13.5002C14.1667 14.9003 14.1667 15.6004 13.8942 16.1351C13.6545 16.6055 13.2721 16.988 12.8016 17.2277C12.2669 17.5002 11.5668 17.5002 10.1667 17.5002H6.5C5.09987 17.5002 4.39981 17.5002 3.86503 17.2277C3.39462 16.988 3.01217 16.6055 2.77249 16.1351C2.5 15.6004 2.5 14.9003 2.5 13.5002V4.16683"
                      stroke="#cccccc"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
};

export default FileUploadDropzone;
